import { FC, useMemo } from "react"
import { useOrdersListQuery } from "../../store"
import { ORDER_FILTER_SCHEMA } from "../../constants"
import { DataGrid, PreviousLocation, RouterLink } from "@/core/components"
import { ADV } from "../../interfaces"
import {
  GridActionsCellItem,
  GridColDef,
  getGridStringOperators,
} from "@mui/x-data-grid-pro"
import parseISO from "date-fns/parseISO/index.js"
import { formatDate, wrapFilterOperator } from "@/core/utils"
import { GRID_PAGE_SIZE_OPTIONS } from "@/core/constants"
import { Preview as PreviewIcon } from "@mui/icons-material"
import { useNavigate, useUrlFilters, useUrlPagination } from "@/core/hooks"

interface IProps {
  agents?: ADV.Agent["id"]
}
export const OrdersTable: FC<IProps> = ({ agents }) => {
  const navigate = useNavigate<PreviousLocation>()
  const { read: readFilters } = useUrlFilters(ORDER_FILTER_SCHEMA)
  const { page, pageSize, setPaginationModel } = useUrlPagination(
    GRID_PAGE_SIZE_OPTIONS[2],
  )
  const ordersFilters = readFilters()
  const { isFetching, data } = useOrdersListQuery({
    queryParams: {
      page: page + 1,
      limit: pageSize,
      ...(agents !== undefined ? { agents } : ordersFilters),
    },
  })

  const columns: GridColDef<ADV.Order>[] = useMemo(
    () => [
      {
        headerName: "ID",
        field: "id",
        flex: 1,
      },
      {
        headerName: "Тип",
        field: "type_name",
        flex: 1,
      },
      {
        headerName: "Формат",
        field: "format_name",
        flex: 1,
      },
      {
        headerName: "Кампания",
        field: "campaign_name",
        flex: 1,
      },
      {
        headerName: "Статус",
        field: "status_name",
        flex: 1,
      },
      {
        headerName: "Комментарий",
        field: "comment",
        flex: 1,
      },
      {
        headerName: "Дата начала",
        field: "date_start",
        valueGetter: ({ value }) => value && parseISO(value),
        valueFormatter: ({ value }) => formatDate(value),
        type: "dateTime",
        flex: 1,
      },
      {
        headerName: "Дата окончания",
        field: "date_finish",
        valueGetter: ({ value }) => value && parseISO(value),
        valueFormatter: ({ value }) => formatDate(value),
        type: "dateTime",
        flex: 1,
      },
      {
        headerName: "Контрагент",
        field: "agent.name",
        valueGetter: ({ row }) => row.agent,
        renderCell: ({ value }) => (
          <RouterLink<PreviousLocation>
            color="secondary"
            title={`${value.name}`}
            to={`../agents/${value.id}`}
            state={{
              from: window.location.pathname,
              title: "К списку заказов",
            }}
          />
        ),
        filterOperators: getGridStringOperators().map((f) =>
          wrapFilterOperator<NonNullable<ADV.Agent["contacts"]>>(
            f,
            (applyFilterFn, params) =>
              applyFilterFn({
                ...params,
                value: params.value?.map((v: ADV.Agent) => v.name).join(),
              }),
            (applyFilterFnV7, value, row, col, apiRef) =>
              applyFilterFnV7(value?.join(), row, col, apiRef),
          ),
        ),
        getApplyQuickFilterFn: (value, col) => {
          return (params) =>
            params.value?.some((v: any) =>
              v?.toString().toLowerCase().includes(value?.name.toLowerCase()),
            )
        },
        flex: 1,
      },
      {
        headerName: "Реф. коды",
        field: "ref_codes",
        flex: 1,
      },
    ],
    [],
  )

  return (
    <DataGrid<ADV.Order>
      columns={columns}
      rows={data?.data ?? []}
      rowCount={data?.count ?? 0}
      loading={isFetching}
      // unstable_headerFilters
      filterMode="client"
      sortingMode="client"
      pagination
      paginationMode="server"
      pageSizeOptions={GRID_PAGE_SIZE_OPTIONS}
      onPaginationModelChange={(model) => setPaginationModel(model)}
      initialState={{
        sorting: {
          sortModel: [{ field: "id", sort: "desc" }],
        },
        pagination: { paginationModel: { page, pageSize } },
      }}
      rowActions={[
        (r) => (
          <GridActionsCellItem
            label="Открыть"
            icon={<PreviewIcon />}
            onClick={() =>
              navigate(r.id.toString(), {
                state: {
                  from: window.location.pathname,
                  title: "К списку заказов",
                },
              })
            }
          />
        ),
      ]}
      // slots={{ toolbar: QuickSearchToolbar }}
    />
  )
}
